/**
 * printQuotation — iter-292 (Feb 2026)
 * ====================================
 * Build and open/download a print-ready PDF for a quotation.
 *
 * Public API:
 *   printQuotation(quotation, { language, mode, download })
 *   - language: UI toolbar language (default 'ar')
 *   - mode: 'ar' | 'en' | 'both'   (default 'both')
 *   - download: true → trigger browser print-to-pdf shortcut
 *
 * Reads unified brand settings from /api/admin/receipt-studio/brand
 * (5-minute in-module cache so repeated prints don't re-fetch).
 */

const FACTORY_BRAND = {
  logo_url:          '/pdf-logo-cloudgm.png',
  accent_from:       '#3B9EDC',
  accent_to:         '#F5A55A',
  brand_name_ar:     'CLOUDGM FZ LLC',
  brand_name_en:     'CLOUDGM FZ LLC',
  footer_sites:      ['cloudgm.com', 'cloudgm.business'],
  footer_phone:      '+971 50 151 1917',
  footer_emails:     ['info@cloudgm.com', 'info@cloudgm.business'],
  footer_tagline_ar: 'شكراً لتعاملكم معنا',
  footer_tagline_en: 'Thank you for your business',
};

const BRAND_CACHE_TTL_MS = 5 * 60 * 1000;
let _brandCache = null;
let _brandCacheAt = 0;

const fetchBrand = async () => {
  const now = Date.now();
  if (_brandCache && (now - _brandCacheAt) < BRAND_CACHE_TTL_MS) return _brandCache;
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
    const r = await fetch(`${base}/api/admin/receipt-studio/brand`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) throw new Error(`brand fetch ${r.status}`);
    const data = await r.json();
    _brandCache = { ...FACTORY_BRAND, ...(data?.data || {}) };
    _brandCacheAt = now;
  } catch {
    _brandCache = FACTORY_BRAND;
    _brandCacheAt = now;
  }
  return _brandCache;
};

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const fmtAed = (n) => Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const fmtDate = (iso, isAr) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(isAr ? 'ar-AE' : 'en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
};

/* ────────────────────────────────────────────────────────────────── */
/* Single-language body renderer                                      */
/* Returns the inner <body> HTML for ONE language.                    */
/* ────────────────────────────────────────────────────────────────── */
const renderBody = (q, isAr, brand) => {
  const t = (ar, en) => (isAr ? ar : en);
  const FROM = brand.accent_from || FACTORY_BRAND.accent_from;
  const TO   = brand.accent_to   || FACTORY_BRAND.accent_to;
  const NAME = isAr ? (brand.brand_name_ar || brand.brand_name_en) : (brand.brand_name_en || brand.brand_name_ar);
  const LOGO = brand.logo_url || FACTORY_BRAND.logo_url;

  const title = isAr ? (q.title_ar || q.title_en || 'عرض سعر')
                     : (q.title_en || q.title_ar || 'Quotation');
  const pkgName = isAr ? (q.package_name_ar || q.package_name_en || '')
                       : (q.package_name_en || q.package_name_ar || '');

  const enabled = q.enabled_sections && q.enabled_sections.length
    ? q.enabled_sections
    : ['services'];

  const services = (q.services || []);
  const hasExcluded = services.some((s) => s.gov_fee_not_included);

  // iter-296 — Split services into two buckets by fee_mode so the PDF
  // renders TWO distinct tables and TWO distinct subtotals, finished
  // with ONE grand total that sums both.
  //  • Detailed table  → columns: # | Service | Duration | Gov Fees | Service Fees | Total
  //  • Combined table  → columns: # | Service | Duration | Service Fees | Total
  const detailedList = services.filter((s) => s.fee_mode !== 'combined');
  const combinedList = services.filter((s) => s.fee_mode === 'combined');

  const buildRow = (s, idx, mode) => {
    const name = isAr
      ? (s.name_ar || s.name_en || '')
      : (s.name_en || s.name_ar || '');
    const dur = isAr
      ? (s.duration_ar || s.duration_en || s.duration || '')
      : (s.duration_en || s.duration_ar || s.duration || '');
    const desc = isAr
      ? (s.description_ar || s.description_en || s.description || '')
      : (s.description_en || s.description_ar || s.description || '');

    const sfFrom = Number(s.service_fee_from || 0);
    const sfTo   = Number(s.service_fee_to   || 0);
    const sfCell = sfFrom === sfTo ? fmtAed(sfTo) : `${fmtAed(sfFrom)} – ${fmtAed(sfTo)}`;

    if (mode === 'combined') {
      // No Gov Fees column at all; Total = Service Fees (gov is 0 by design).
      return `
      <tr>
        <td class="idx">${idx + 1}</td>
        <td>
          <div class="svc-name">${esc(name)}</div>
          ${desc ? `<div class="svc-desc">${esc(desc)}</div>` : ''}
        </td>
        <td class="center">${esc(dur || '—')}</td>
        <td class="num">${sfCell}</td>
        <td class="num total">${sfCell}</td>
      </tr>`;
    }

    // Detailed row — Gov + Service columns.
    let gfCell;
    if (s.gov_fee_not_included) {
      gfCell = `<span style="color:${TO};font-style:italic;font-size:9px;">${esc(t('غير متضمنة', 'Not included'))}</span>`;
    } else {
      const f = Number(s.gov_fee_from || 0), t2 = Number(s.gov_fee_to || 0);
      gfCell = f === t2 ? fmtAed(t2) : `${fmtAed(f)} – ${fmtAed(t2)}`;
    }
    const gf1Row = s.gov_fee_not_included ? 0 : Number(s.gov_fee_from || 0);
    const gf2Row = s.gov_fee_not_included ? 0 : Number(s.gov_fee_to   || 0);
    const rowMin = gf1Row + sfFrom;
    const rowMax = gf2Row + sfTo;
    const rowCell = rowMin === rowMax ? fmtAed(rowMax) : `${fmtAed(rowMin)} – ${fmtAed(rowMax)}`;
    return `
      <tr>
        <td class="idx">${idx + 1}</td>
        <td>
          <div class="svc-name">${esc(name)}</div>
          ${desc ? `<div class="svc-desc">${esc(desc)}</div>` : ''}
        </td>
        <td class="center">${esc(dur || '—')}</td>
        <td class="num">${gfCell}</td>
        <td class="num">${sfCell}</td>
        <td class="num total">${rowCell}</td>
      </tr>`;
  };

  // Build each table body.
  const detailedRows = detailedList.map((s, i) => buildRow(s, i, 'detailed')).join('');
  const combinedRows = combinedList.map((s, i) => buildRow(s, i, 'combined')).join('');

  // Compute each table's subtotal (range-aware: {min, max}).
  const sumServices = (list, includeGov) => {
    let min = 0, max = 0;
    for (const s of list) {
      const sfFrom = Number(s.service_fee_from || 0);
      const sfTo   = Number(s.service_fee_to   || 0);
      const gfFrom = includeGov && !s.gov_fee_not_included ? Number(s.gov_fee_from || 0) : 0;
      const gfTo   = includeGov && !s.gov_fee_not_included ? Number(s.gov_fee_to   || 0) : 0;
      min += sfFrom + gfFrom;
      max += sfTo   + gfTo;
    }
    return { min, max };
  };
  const subDetailed = sumServices(detailedList, true);
  const subCombined = sumServices(combinedList, false);
  const grand = { min: subDetailed.min + subCombined.min, max: subDetailed.max + subCombined.max };

  const fmtRange = (r) => (r.min === r.max ? fmtAed(r.max) : `${fmtAed(r.min)} – ${fmtAed(r.max)}`);

  const terms = isAr ? (q.terms_ar || q.terms_en || '') : (q.terms_en || q.terms_ar || '');
  const termLines = terms.split('\n').filter((l) => l.trim())
    .map((l) => `<li>${esc(l.replace(/^[•\-\*]\s*/, ''))}</li>`).join('');

  const customerNotes = isAr
    ? (q.customer_notes_ar || q.customer_notes_en || '')
    : (q.customer_notes_en || q.customer_notes_ar || '');

  const notIncludedBanner = hasExcluded ? `
    <div class="banner">
      <strong>${esc(t('ملاحظة مهمة:', 'Important note:'))}</strong>
      ${esc(t(
        'الرسوم الحكومية الموضوعة في بعض الخدمات غير متضمنة في عرض السعر. يتم تسديدها مباشرة للجهة الحكومية المعنية وفق التسعير الرسمي عند التنفيذ.',
        'The government fees marked above are NOT included in this quotation. They are paid directly to the respective authority at the official rate at the time of execution.',
      ))}
    </div>` : '';

  return `
    <div class="brand-strip"></div>
    <div class="header">
      <img class="logo" src="${esc(LOGO)}" alt="" onerror="this.style.display='none'">
      <div class="brand">
        <h1>${esc(NAME || '')}</h1>
      </div>
      <div class="meta">
        <div class="badge">${esc(t('عرض سعر', 'QUOTATION'))}</div>
        <div class="no">${esc(q.quotation_no || '')}</div>
        <div class="date">${esc(t('التاريخ', 'Date'))}: ${fmtDate(q.created_at, isAr)}</div>
      </div>
    </div>

    <div class="title-row">
      <h2>${esc(title)}</h2>
      ${pkgName ? `<p style="margin:4px 0 0;font-size:11px;color:var(--from);font-weight:600;">${esc(t('الباكج', 'Package'))}: ${esc(pkgName)}</p>` : ''}
      ${q.valid_until ? `<p style="margin:2px 0 0;font-size:10px;color:var(--ink-600);">${esc(t('ساري حتى', 'Valid until'))}: ${fmtDate(q.valid_until, isAr)}</p>` : ''}
    </div>

    <div class="customer">
      <div><div class="lbl">${esc(t('العميل', 'Customer'))}</div><div class="val">${esc(q.customer_name || '—')}</div></div>
      <div><div class="lbl">${esc(t('الشركة', 'Company'))}</div><div class="val">${esc(q.customer_company || '—')}</div></div>
      <div><div class="lbl">${esc(t('الهاتف', 'Phone'))}</div><div class="val" dir="ltr">${esc(q.customer_phone || '—')}</div></div>
      <div><div class="lbl">${esc(t('البريد', 'Email'))}</div><div class="val" dir="ltr">${esc(q.customer_email || '—')}</div></div>
    </div>

    ${(enabled.includes('services') || enabled.includes('fixed_services')) ? `
      ${detailedList.length ? `
        <div class="table-title">${esc(t('الخدمات (رسوم حكومية + رسوم خدمة)', 'Services (Government + Service Fees)'))}</div>
        <table class="services">
          <thead>
            <tr>
              <th class="center">#</th>
              <th>${esc(t('الخدمة', 'Service'))}</th>
              <th class="center">${esc(t('المدة', 'Duration'))}</th>
              <th class="num">${esc(t('رسوم حكومية', 'Gov. Fees'))}</th>
              <th class="num">${esc(t('رسوم الخدمة', 'Service Fees'))}</th>
              <th class="num">${esc(t('الإجمالي', 'Total'))}</th>
            </tr>
          </thead>
          <tbody>${detailedRows}</tbody>
          <tfoot>
            <tr class="subtotal">
              <td colspan="5" style="text-align:${isAr ? 'left' : 'right'};">
                ${esc(t('المجموع الفرعي للجدول', 'Table Subtotal'))}
              </td>
              <td class="num total">${fmtRange(subDetailed)} AED</td>
            </tr>
          </tfoot>
        </table>
        ${notIncludedBanner}
      ` : ''}

      ${combinedList.length ? `
        <div class="table-title">${esc(t('خدمات بسعر شامل', 'Services (Combined Price)'))}</div>
        <table class="services combined-tbl">
          <thead>
            <tr>
              <th class="center">#</th>
              <th>${esc(t('الخدمة', 'Service'))}</th>
              <th class="center">${esc(t('المدة', 'Duration'))}</th>
              <th class="num">${esc(t('رسوم الخدمة', 'Service Fees'))}</th>
              <th class="num">${esc(t('الإجمالي', 'Total'))}</th>
            </tr>
          </thead>
          <tbody>${combinedRows}</tbody>
          <tfoot>
            <tr class="subtotal">
              <td colspan="4" style="text-align:${isAr ? 'left' : 'right'};">
                ${esc(t('المجموع الفرعي للجدول', 'Table Subtotal'))}
              </td>
              <td class="num total">${fmtRange(subCombined)} AED</td>
            </tr>
          </tfoot>
        </table>
      ` : ''}

      ${(detailedList.length || combinedList.length) ? `
        <div class="totals">
          <div class="totals-box">
            ${detailedList.length && combinedList.length ? `
              <div class="totals-row sub"><span>${esc(t('المجموع — جدول رسوم تفصيلية', 'Subtotal — Detailed table'))}</span><span class="num">${fmtRange(subDetailed)} AED</span></div>
              <div class="totals-row sub"><span>${esc(t('المجموع — جدول سعر شامل', 'Subtotal — Combined table'))}</span><span class="num">${fmtRange(subCombined)} AED</span></div>
            ` : ''}
            <div class="totals-row grand"><span>${esc(t('المجموع الكلي', 'Grand Total'))}</span><span class="num">${fmtRange(grand)} AED</span></div>
          </div>
        </div>
      ` : ''}
    ` : ''}

    ${enabled.includes('customer_notes') && customerNotes ? `
      <div class="cust-notes">
        <h3>${esc(t('ملاحظات', 'Notes'))}</h3>
        <p>${esc(customerNotes).replace(/\n/g, '<br>')}</p>
      </div>` : ''}

    ${enabled.includes('terms') && termLines ? `
      <div class="terms">
        <h3>${esc(t('الشروط والأحكام', 'Terms & Conditions'))}</h3>
        <ul>${termLines}</ul>
      </div>` : ''}
  `;
};

const BASE_CSS = (FROM, TO, isDir) => `
  :root {
    --from: ${FROM}; --to: ${TO};
    --ink-900: #0f172a; --ink-600: #334155; --ink-400: #64748b; --ink-200: #e2e8f0;
    --paper: #ffffff; --tint: #f8fafc;
    --brand-grad: linear-gradient(90deg, var(--from) 0%, var(--to) 100%);
  }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  html, body { margin: 0; padding: 0; background: var(--tint); color: var(--ink-900); font-size: 11px; line-height: 1.5; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  @page { size: A4; margin: 10mm; }

  .brand-strip { height: 6px; background: var(--brand-grad); margin: 0 0 24px 0; border-radius: 2px; }
  .header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 24px; }
  .logo { height: 56px; width: auto; }
  .brand h1 { margin: 0; font-size: 15px; font-weight: 700; color: var(--ink-900); }
  .meta { text-align: ${isDir === 'rtl' ? 'left' : 'right'}; }
  .meta .badge { display: inline-block; padding: 4px 10px; border-radius: 999px; background: var(--brand-grad); color: #fff; font-size: 10px; font-weight: 700; letter-spacing: 0.5px; margin-bottom: 6px; }
  .meta .no { font-family: 'Inter', monospace; font-size: 12px; font-weight: 700; color: var(--from); }
  .meta .date { font-size: 10px; color: var(--ink-400); margin-top: 2px; }

  .title-row { background: linear-gradient(90deg, ${FROM}0D 0%, ${TO}0D 100%); border-${isDir === 'rtl' ? 'right' : 'left'}: 3px solid var(--from); padding: 10px 16px; border-radius: 4px; margin-bottom: 16px; }
  .title-row h2 { margin: 0; font-size: 13px; color: var(--ink-900); font-weight: 700; }

  .customer { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 20px; padding: 12px 14px; border: 1px solid var(--ink-200); border-radius: 6px; background: var(--tint); }
  .customer .lbl { font-size: 9px; color: var(--ink-400); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; }
  .customer .val { font-size: 11px; color: var(--ink-900); font-weight: 600; }

  .services { width: 100%; border-collapse: collapse; margin-bottom: 20px; border-radius: 6px; overflow: hidden; }
  .services thead { background: var(--brand-grad); color: #fff; }
  .services th { padding: 10px 8px; font-size: 10px; font-weight: 700; text-align: ${isDir === 'rtl' ? 'right' : 'left'}; text-transform: uppercase; letter-spacing: 0.3px; }
  .services th.center { text-align: center; }
  .services th.num { text-align: ${isDir === 'rtl' ? 'left' : 'right'}; }
  .services td { padding: 10px 8px; border-bottom: 1px solid var(--ink-200); vertical-align: top; }
  .services td.idx { font-family: 'Inter', monospace; color: var(--ink-400); font-size: 10px; width: 24px; text-align: center; }
  .services td.center { text-align: center; font-size: 10px; color: var(--ink-600); }
  .services td.num { text-align: ${isDir === 'rtl' ? 'left' : 'right'}; font-family: 'Inter', monospace; font-size: 10px; color: var(--ink-600); white-space: nowrap; }
  .services td.num.total { color: var(--from); font-weight: 700; font-size: 11px; }
  .services tfoot tr.subtotal td { background: ${FROM}10; border-top: 2px solid ${FROM}30; font-weight: 700; padding: 10px 8px; color: var(--ink-900); font-size: 10.5px; }
  .services tfoot tr.subtotal td.total { color: var(--from); font-size: 12px; }
  .table-title { margin: 16px 0 8px; font-size: 12px; font-weight: 700; color: var(--from); font-family: 'Cairo', 'Inter', sans-serif; padding-${isDir === 'rtl' ? 'right' : 'left'}: 8px; border-${isDir === 'rtl' ? 'right' : 'left'}: 3px solid var(--from); }
  .services td.num.combined { text-align: center; background: ${FROM}08; }
  .services td.num.combined .combined-label { font-family: 'Cairo', 'Inter', sans-serif; color: var(--ink-400); font-size: 9px; margin-${isDir === 'rtl' ? 'left' : 'right'}: 6px; }
  .services td.num.combined .combined-amt { font-family: 'Inter', monospace; font-weight: 700; color: var(--from); }
  .svc-name { font-size: 11px; font-weight: 600; color: var(--ink-900); }
  .svc-desc { font-size: 9px; color: var(--ink-400); margin-top: 2px; line-height: 1.4; }

  .banner { margin-bottom: 16px; padding: 10px 14px; background: ${TO}1A; border: 1px dashed ${TO}; border-radius: 6px; font-size: 10px; color: var(--ink-600); line-height: 1.6; }
  .banner strong { color: ${TO}; }

  .totals { display: flex; justify-content: flex-end; margin-bottom: 20px; }
  .totals-box { min-width: 260px; border: 2px solid var(--to); border-radius: 6px; overflow: hidden; }
  .totals-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 8px 12px; font-size: 10px; }
  .totals-row > span:first-child { flex: 1 1 auto; }
  .totals-row.sub { background: ${TO}0D; }
  .totals-row.grand { background: var(--brand-grad); color: #fff; font-weight: 700; font-size: 12px; }
  .totals-row .num { font-family: 'Inter', monospace; direction: ltr; }

  .cust-notes { margin-bottom: 16px; padding: 12px 14px; background: ${FROM}0D; border: 1px solid ${FROM}22; border-radius: 6px; }
  .cust-notes h3 { margin: 0 0 6px; font-size: 11px; font-weight: 700; color: var(--from); }
  .cust-notes p { margin: 0; font-size: 10px; color: var(--ink-600); line-height: 1.6; }

  .terms { margin-bottom: 20px; padding: 12px 14px; background: ${FROM}08; border: 1px solid ${FROM}22; border-radius: 6px; }
  .terms h3 { margin: 0 0 6px; font-size: 11px; font-weight: 700; color: var(--from); }
  .terms ul { margin: 0; padding-${isDir === 'rtl' ? 'right' : 'left'}: 18px; font-size: 10px; color: var(--ink-600); }
  .terms li { margin-bottom: 3px; }

  .footer { margin-top: 30px; padding-top: 14px; border-top: 1px solid var(--ink-200); text-align: center; color: var(--ink-400); font-size: 9px; }
  .footer .bar { height: 2px; background: var(--brand-grad); margin-bottom: 10px; border-radius: 999px; }
  .footer .tag { font-weight: 700; color: var(--from); margin-bottom: 4px; font-size: 11px; }
  .footer .row { margin: 2px 0; direction: ltr; }

  @media print {
    body { background: var(--paper); }
    .no-print { display: none !important; }
    /* iter-295 — force the browser to keep brand gradients, header fills,
       title-row tints and totals-box backgrounds exactly as previewed.
       Without this Chromium/Edge strip background colors to save ink. */
    *, *::before, *::after {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    .services thead,
    .totals-box .grand,
    .brand-strip,
    .footer .bar,
    .meta .badge,
    .title-row {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }

  .toolbar { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; z-index: 100; }
  .toolbar button { padding: 8px 16px; border: none; border-radius: 6px; background: var(--brand-grad); color: #fff; font-weight: 700; cursor: pointer; font-size: 12px; }
  .toolbar button.secondary { background: #fff; color: var(--ink-900); border: 1px solid var(--ink-200); }
`;

const buildFooter = (brand, isAr) => {
  const TAG = isAr ? (brand.footer_tagline_ar || '') : (brand.footer_tagline_en || '');
  const NAME = isAr ? (brand.brand_name_ar || '') : (brand.brand_name_en || '');
  const SITES  = (brand.footer_sites  || []).join(' · ');
  const PHONE  = brand.footer_phone || '';
  const EMAILS = (brand.footer_emails || []).join(' · ');
  return `
    <div class="footer">
      <div class="bar"></div>
      ${TAG ? `<div class="tag">${esc(TAG)}</div>` : ''}
      <div class="row">🌐 ${esc(SITES)}</div>
      <div class="row">📞 ${esc(PHONE)}</div>
      <div class="row">✉ ${esc(EMAILS)}</div>
      <div class="row" style="margin-top:6px;">${esc(NAME)}</div>
    </div>`;
};

/* ────────────────────────────────────────────────────────────────── */
/* Modes                                                              */
/* ar / en: full-page single language                                 */
/* both: two sections stacked; AR page then page-break then EN page   */
/* ────────────────────────────────────────────────────────────────── */
const buildHtml = (q, brand, mode, uiLang) => {
  const uiAr = (uiLang || 'ar').toLowerCase().startsWith('ar');
  const FROM = brand.accent_from || FACTORY_BRAND.accent_from;
  const TO   = brand.accent_to   || FACTORY_BRAND.accent_to;

  const sections = [];
  const addSection = (isAr) => {
    const dir = isAr ? 'rtl' : 'ltr';
    const bodyHtml = renderBody(q, isAr, brand);
    const footerHtml = buildFooter(brand, isAr);
    // iter-297 — Wrap in <table> so Chromium repeats the <tfoot> footer
    // on every physical printed page, not just the last page.
    sections.push(`
      <section class="qt-page" dir="${dir}" lang="${isAr ? 'ar' : 'en'}" style="font-family: ${isAr ? "'Cairo'" : "'Inter'"}, sans-serif;">
        <table class="print-layout">
          <tfoot><tr><td>${footerHtml}</td></tr></tfoot>
          <tbody><tr><td>${bodyHtml}</td></tr></tbody>
        </table>
      </section>`);
  };

  if (mode === 'en') addSection(false);
  else if (mode === 'ar') addSection(true);
  else { addSection(true); addSection(false); }  // both

  const printLabel = uiAr ? '🖨️ طباعة' : '🖨️ Print';
  const closeLabel = uiAr ? 'إغلاق' : 'Close';

  return `<!doctype html>
<html lang="${uiAr ? 'ar' : 'en'}" dir="${uiAr ? 'rtl' : 'ltr'}">
<head>
<meta charset="utf-8">
<title>${esc(q.quotation_no || 'Quotation')}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Inter:wght@400;600;700&display=swap">
<style>
  ${BASE_CSS(FROM, TO, 'rtl')}
  /* RTL variant inherits, LTR overrides below where needed */
  .qt-page { max-width: 820px; margin: 0 auto; padding: 32px; background: var(--paper); page-break-after: always; }
  .qt-page:last-child { page-break-after: auto; }
  /* iter-297 — table-based layout so <tfoot> repeats on every print page */
  .print-layout { width: 100%; border-collapse: collapse; }
  .print-layout > tbody > tr > td { padding: 0; }
  .print-layout > tfoot { display: table-footer-group; }
  .print-layout > tfoot > tr > td { padding: 0; }
  .qt-page[dir="ltr"] .meta { text-align: right; }
  .qt-page[dir="ltr"] .services th { text-align: left; }
  .qt-page[dir="ltr"] .services th.center { text-align: center; }
  .qt-page[dir="ltr"] .services th.num { text-align: right; }
  .qt-page[dir="ltr"] .services td.num { text-align: right; }
  .qt-page[dir="ltr"] .title-row { border-left: 3px solid var(--from); border-right: none; }
  .qt-page[dir="ltr"] .terms ul { padding-left: 18px; padding-right: 0; }
</style>
</head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">${esc(printLabel)}</button>
    <button class="secondary" onclick="window.close()">${esc(closeLabel)}</button>
  </div>
  ${sections.join('\n')}
</body>
</html>`;
};

export const printQuotation = async (quotation, opts = {}) => {
  if (!quotation) return;
  const brand = await fetchBrand();
  const mode  = opts.mode || 'both';
  const html  = buildHtml(quotation, brand, mode, opts.language);

  const win = window.open('', '_blank', 'width=900,height=1100');
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
  setTimeout(() => {
    try {
      win.focus();
      if (opts.download) win.print();
    } catch { /* noop */ }
  }, 500);
};
